import { Controller } from "@hotwired/stimulus"
import { post } from "@rails/request.js"
import { isEqual, omit } from "lodash";

// Connects to data-controller="indicaciones-medicas-office"
export default class extends Controller {
  static targets = ["submitButton", "submitSpinner", "submitLabel"]

  static values = {
    registro: Number,
    hoja: Number,
    currentDate: String,
    currentUser: String
  }

  initialize() {
    this.indicacionesOffice = [];
    this.isSubmitting = false;
  }

  connect() {
    this.initialIndicacionesOffice = [];

    this.debug('connect:start', {
      formId: this.element.id,
      currentDate: this.currentDateValue,
      registro: this.registroValue,
      hoja: this.hojaValue,
      datasetLength: this.element.dataset.indicacionesOffice?.length || 0,
      renderedCheckboxes: this.checkboxIds().length
    });

    if (this.element.dataset.indicacionesOffice.length) {
      const indicacionesOffice = JSON.parse(this.element.dataset.indicacionesOffice)
      this.debug('connect:raw dataset parsed', {
        totalRecords: indicacionesOffice.length,
        rawRecords: indicacionesOffice
      });

      indicacionesOffice.forEach(indicacionOffice => {
        const parsedIndicacion = JSON.parse(indicacionOffice)
        const horaColocString = this.extractHour(parsedIndicacion.HoraColoc)

        const indicacionMedicaOffice = {
          MaterialCodigo: parsedIndicacion.MaterialCodigo,
          farmaco: parsedIndicacion.farmaco || '',
          Interno: parsedIndicacion.Interno,
          HoraColoc: horaColocString,
          Fecha: parsedIndicacion.Fecha,
          Usuario: parsedIndicacion.Usuario || this.currentUserValue,
          destroyable: false
        };

        this.debug('connect:normalized office record', {
          parsedIndicacion,
          normalizedRecord: indicacionMedicaOffice,
          expectedCheckboxId: this.checkboxIdFor(indicacionMedicaOffice)
        });

        this.indicacionesOffice.push(indicacionMedicaOffice);
        this.initialIndicacionesOffice.push({ ...indicacionMedicaOffice });
      });
    }

    this.indicacionesOffice.forEach((indicacionOffice, index) => {
      try {
        const id = this.checkboxIdFor(indicacionOffice);
        const el = document.getElementById(id);

        if (!el) {
          this.warn('connect:checkbox not found', {
            index,
            expectedCheckboxId: id,
            indicacionOffice,
            availableCheckboxIdsForInterno: this.checkboxIdsForInterno(indicacionOffice.Interno)
          });
          return;
        }

        el.checked = true;
        this.debug('connect:checkbox checked', {
          index,
          checkboxId: id,
          indicacionOffice
        });
      } catch (e) {
        console.error('[indicaciones-medicas-office] connect:error while hydrating checkbox', {
          index,
          indicacionOffice,
          error: e
        });
      }
    });

    this.debug('connect:end', {
      hydratedRecords: this.indicacionesOffice,
      initialRecords: this.initialIndicacionesOffice
    });
  }

  setIndicacionOffice(e) {
    e.preventDefault();

    const indicacionMedicaDetalle = JSON.parse(e.target.dataset.indicacionMedicaDetalle);
    const horaColoc = e.target.value;
    const indicacionMedicaOffice = {
      MaterialCodigo: indicacionMedicaDetalle.MaterialCodigo,
      farmaco: this.slugify(indicacionMedicaDetalle.farmaco || '') || '',
      Interno: indicacionMedicaDetalle.Interno,
      HoraColoc: horaColoc,
      Usuario: this.currentUserValue,
      Fecha: this.currentDateValue,
      destroyable: false
    };

    const isChecked = e.target.checked;

    const indicacionOfficeIndex = this.indicacionesOffice.findIndex(ind =>
      isEqual(omit(ind, ['Usuario', 'destroyable']), omit(indicacionMedicaOffice, ['Usuario', 'destroyable']))
    );

    this.debug('change:checkbox toggled', {
      checkboxId: e.target.id,
      checked: isChecked,
      rawValue: e.target.value,
      rawDetalle: indicacionMedicaDetalle,
      normalizedRecord: indicacionMedicaOffice,
      existingIndex: indicacionOfficeIndex,
      currentRecordsBeforeChange: this.indicacionesOffice
    });

    if (isChecked) {
      if (indicacionOfficeIndex === -1) {
        this.indicacionesOffice.push(indicacionMedicaOffice);
      } else {
        this.indicacionesOffice[indicacionOfficeIndex].destroyable = false;
      }
    } else {
      if (indicacionOfficeIndex > -1) {
        const wasInitial = this.initialIndicacionesOffice.some(ind =>
          isEqual(omit(ind, ['Usuario', 'destroyable']), omit(indicacionMedicaOffice, ['Usuario', 'destroyable']))
        );

        if (wasInitial) {
          this.indicacionesOffice[indicacionOfficeIndex].destroyable = true;
        } else {
          this.indicacionesOffice.splice(indicacionOfficeIndex, 1);
        }
      }
    }

    this.debug('change:records after toggle', {
      checkboxId: e.target.id,
      checked: isChecked,
      currentRecordsAfterChange: this.indicacionesOffice
    });
  }

  async submit(e) {
    e.preventDefault();

    if (this.isSubmitting || e.currentTarget.classList.contains('disabled')) {
      return;
    }

    this.toggleSubmittingState(true);
    document.dispatchEvent(new CustomEvent('spinner:show'));

    try {
      const indicacionesOfficePayload = this.indicacionesOffice.map((indicacion) => ({
        ...indicacion,
        HoraColoc: this.localHourToUtcHour(indicacion.HoraColoc)
      }));

      this.debug('submit:payload ready', {
        currentDate: this.currentDateValue,
        rawRecords: this.indicacionesOffice,
        payload: indicacionesOfficePayload
      });

      const response = await post('/indicaciones_medicas_office/batch_create', {
        responseKind: 'turbo-stream',
        body: {
          Registro: this.registroValue,
          Hoja: this.hojaValue,
          current_date: this.currentDateValue,
          indicaciones_medicas_office: indicacionesOfficePayload
        }
      })

      this.debug('submit:request completed', {
        response
      });
    } finally {
      this.toggleSubmittingState(false);
      document.dispatchEvent(new CustomEvent('spinner:hide'));
    }
  }

  toggleSubmittingState(submitting) {
    this.isSubmitting = submitting;

    this.submitButtonTargets.forEach((button) => {
      button.classList.toggle('disabled', submitting);
      button.setAttribute('aria-disabled', submitting ? 'true' : 'false');
      button.style.pointerEvents = submitting ? 'none' : '';
    });

    this.submitSpinnerTargets.forEach((spinner) => {
      spinner.classList.toggle('d-none', !submitting);
    });

    this.submitLabelTargets.forEach((label) => {
      label.classList.toggle('d-none', submitting);
    });

    this.element.querySelectorAll('input, select, textarea, button').forEach((control) => {
      if (control.type === 'hidden') {
        return;
      }

      if (submitting) {
        control.dataset.wasDisabled = control.disabled ? 'true' : 'false';
        control.disabled = true;
      } else if (control.dataset.wasDisabled === 'false') {
        control.disabled = false;
        delete control.dataset.wasDisabled;
      } else {
        delete control.dataset.wasDisabled;
      }
    });
  }

  slugify(text) {
    if (!text) {
      return '';
    }
    return text
      .toString()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .trim()
      .replace(/\s+/g, '-')
      .replace(/[^\w-]+/g, '-')
      .replace(/--+/g, '-');
  }

  extractHour(rawHour) {
    if (!rawHour) {
      return '';
    }

    const utcHour = this.extractUtcHour(rawHour);
    if (utcHour === null) {
      return '';
    }

    // HoraColoc se persiste en UTC y el formulario opera en hora local Argentina (UTC-3).
    const argentinaHour = (utcHour + 21) % 24;
    return argentinaHour === 0 ? '00' : argentinaHour.toString();
  }

  extractUtcHour(rawHour) {
    const stringHour = String(rawHour);
    const date = new Date(stringHour);

    if (!Number.isNaN(date.getTime())) {
      return date.getUTCHours();
    }

    const match = stringHour.match(/(\d{2}):(\d{2}):(\d{2})/);
    if (match) {
      return parseInt(match[1], 10);
    }

    return null;
  }

  localHourToUtcHour(localHour) {
    const parsedHour = parseInt(localHour, 10);

    if (Number.isNaN(parsedHour)) {
      return localHour;
    }

    // El formulario opera en hora Argentina (UTC-3); para persistir se envía hora UTC.
    return ((parsedHour + 3) % 24).toString();
  }

  checkboxIdFor(indicacionOffice) {
    return `${indicacionOffice.MaterialCodigo || indicacionOffice.farmaco || ''}-${indicacionOffice.Interno}-${indicacionOffice.HoraColoc}`;
  }

  checkboxIdsForInterno(interno) {
    return this.checkboxIds().filter((id) => id.includes(`-${interno}-`));
  }

  checkboxIds() {
    return Array.from(this.element.querySelectorAll('input[type="checkbox"]')).map((checkbox) => checkbox.id);
  }

  debug(message, payload = {}) {
    console.log(`[indicaciones-medicas-office] ${message}`, payload);
  }

  warn(message, payload = {}) {
    console.warn(`[indicaciones-medicas-office] ${message}`, payload);
  }
}
